// src/components/layout/Footer/SubscribeForm.tsx
import React, { useState, type FormEvent } from 'react';
import { Box, TextField } from '@mui/material';
import { colors } from '../../../styles/theme';
import CustomButton from '../../ui/Button/Custombutton';
import translations from '../../../translations/en.json';
import { useTheme } from '../../../../core/providers/ThemeProvider';
import theme from '../../../styles/theme/theme';

interface SubscribeFormProps {
  onSubscribe?: (email: string) => Promise<void> | void;
}

export const SubscribeForm: React.FC<SubscribeFormProps> = ({ onSubscribe }) => {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');

  const formColors = {
    inputBg: colors.neutral.white,
    inputBorder: colors.neutral.gray200 || '#DFDFDF',
    inputText: colors.neutral.gray600 || '#5C5C5C',
    buttonBg: colors.neutral.white,
    buttonText: '#5D1036', 
    buttonHover: colors.neutral.gray100 || '#F5F5F5',
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (onSubscribe) {
      await onSubscribe(email);
    }
    setEmail('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: { xs: '100%', md: '100%', lg: 300 },
        maxWidth: { xs: '100%', md: '100%', lg: 340, xl: 360, xxl: 380 },
        width: { xs: '100%', md: '100%', lg: 'auto' },
        flex: { lg: '0 0 auto' },
      }}
    >
      {/* Title */}
      <Box
        component="h3"
        sx={{
          ...theme.typography.sectionHeading,
          textAlign: 'left',
          fontSize: { xs: 13, sm: 14, md: 16, lg: 18 },
          lineHeight: { xs: '19px', sm: '21px', md: '24px', lg: '27px' },
          m: 0,
          mb: 0.5,
        }}
      >
        {translations.footer.subscribe.title}
      </Box>

      {/* Description */}
      <Box
        sx={{
          ...theme.typography.bodyText,
          fontSize: { xs: 11, sm: 12, md: 13, lg: 16 },
          lineHeight: { xs: '16px', sm: '18px', md: '19px', lg: '24px' },
          mb: 0.5,
        }}
      >
        {translations.footer.subscribe.description}
      </Box>

      {/* Form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'column', lg: 'row' },
        }}
      >
        <TextField
          type="email"
          placeholder={translations.footer.subscribe.placeholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          sx={{
            flex: { xs: 1, sm: 1, lg: 'initial' },
            width: { xs: '100%', sm: '100%', lg: 280 },
            '& .MuiOutlinedInput-root': {
              bgcolor: formColors.inputBg,
              borderRadius: 1,
              height: { xs: 34, md: 38, lg: 42 },
              '& fieldset': {
                borderColor: formColors.inputBorder,
              },
              '&:hover fieldset': {
                borderColor: formColors.inputBorder,
              },
              '&.Mui-focused fieldset': {
                borderColor: formColors.inputBorder,
                borderWidth: 1,
              },
            },
            '& input': {
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: 11, md: 12, lg: 13 },
              fontWeight: 500,
              color: formColors.inputText,
              padding: { xs: '8px 10px', lg: '10px 14px' },
            },
            '& input::placeholder': {
              color: formColors.inputText,
              opacity: 1,
            },
          }}
        />
        <CustomButton
          type="submit"
          buttonText={translations.footer.subscribe.button}
          color="primary"
          size="md"
          sx={{
            width: { xs: '100%', sm: '100%', lg: 'auto' },
            minWidth: { lg: 141 },
            height: { xs: 34, md: 38, lg: 42 },
            bgcolor: formColors.buttonBg,
            color: formColors.buttonText,
            fontFamily: theme.typography.fontFamily,
            fontSize: { xs: 12, md: 13, lg: 15 },
            fontWeight: 600,
            borderRadius: 1.25,
            '&:hover': {
              bgcolor: formColors.buttonHover,
            },
          }}
        />
      </Box>
    </Box>
  );
};